// Cycle configuration, essay questions, and the scoring rubric.
//
// These three screens replace what used to be source edits every year:
// ApplicationController::YEAR, AppController::START_DATE, direct SQL against `essays`, and a
// chain of `case` statements over income labels.

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type AdminCycle } from '../../api.ts';
import { Notice, ErrorNotice, Loading, Field, formatDate, TextInput, Select, TextArea } from '../../components/Bits.tsx';
import { AdminChrome, useAdminSession } from './AdminChrome.tsx';

interface Essay {
  id: number;
  question: string;
  max_length: number;
  position: number;
  active: boolean;
  answer_count: number | string;
}

interface EmailStatus {
  configured: boolean;
  host: string | null;
  port: number;
  user: string | null;
  from: string;
  hasPassword: boolean;
}

interface AdminAccount {
  id: number;
  email: string;
  name: string | null;
  role: 'reviewer' | 'admin';
  lastLoginAt: string | null;
  disabledAt: string | null;
}

interface RubricOption {
  id: number;
  income?: string;
  siblings?: string;
  points: number;
  active: boolean;
}

export function AdminSettings() {
  const { data: session, isPending: sessionPending } = useAdminSession();
  const queryClient = useQueryClient();

  const cycles = useQuery({
    queryKey: ['admin', 'cycles'],
    queryFn: () => api.get<{ cycles: AdminCycle[] }>('/api/admin/cycles'),
    enabled: Boolean(session?.admin),
  });
  const essays = useQuery({
    queryKey: ['admin', 'essays'],
    queryFn: () => api.get<{ essays: Essay[] }>('/api/admin/essays'),
    enabled: Boolean(session?.admin),
  });
  const rubric = useQuery({
    queryKey: ['admin', 'rubric'],
    queryFn: () => api.get<{ income: RubricOption[]; siblings: RubricOption[] }>('/api/admin/rubric'),
    enabled: Boolean(session?.admin?.canConfigure),
  });

  const refresh = (key: string) => () =>
    queryClient.invalidateQueries({ queryKey: ['admin', key] });

  const setCycle = useMutation({
    mutationFn: (input: { id: number; body: Record<string, unknown> }) =>
      api.put(`/api/admin/cycles/${input.id}`, input.body),
    onSuccess: refresh('cycles'),
  });

  const [newQuestion, setNewQuestion] = useState('');
  const [newLimit, setNewLimit] = useState('1000');

  const addEssay = useMutation({
    mutationFn: (cycleId: number) =>
      api.post('/api/admin/essays', {
        cycleId,
        question: newQuestion,
        maxLength: Number(newLimit),
        position: (essays.data?.essays.length ?? 0) + 1,
      }),
    onSuccess: () => { setNewQuestion(''); refresh('essays')(); },
  });

  const retireEssay = useMutation({
    mutationFn: (essayId: number) => api.del(`/api/admin/essays/${essayId}`),
    onSuccess: refresh('essays'),
  });

  const admins = useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => api.get<{ admins: AdminAccount[] }>('/api/admin/admins'),
    enabled: Boolean(session?.admin?.canConfigure),
  });

  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', password: '', role: 'reviewer' });

  const addAdmin = useMutation({
    mutationFn: () => api.post('/api/admin/admins', newAdmin),
    onSuccess: () => {
      setNewAdmin({ email: '', name: '', password: '', role: 'reviewer' });
      refresh('admins')();
    },
  });

  const changeAdmin = useMutation({
    mutationFn: (input: { id: number; body: Record<string, unknown> }) =>
      api.put(`/api/admin/admins/${input.id}`, input.body),
    onSuccess: refresh('admins'),
  });

  const email = useQuery({
    queryKey: ['admin', 'email'],
    queryFn: () => api.get<EmailStatus>('/api/admin/email'),
    enabled: Boolean(session?.admin?.canConfigure),
  });

  const [testTo, setTestTo] = useState('');
  const [testKind, setTestKind] = useState('plain');
  const [mailResult, setMailResult] = useState<string | null>(null);

  const checkMail = useMutation({
    mutationFn: () => api.post<{ message: string }>('/api/admin/email/check'),
    onSuccess: (r) => setMailResult(r.message),
  });

  const sendTest = useMutation({
    mutationFn: () => api.post<{ message: string }>('/api/admin/email/test', {
      to: testTo, kind: testKind,
    }),
    onSuccess: (r) => setMailResult(r.message),
  });

  const [points, setPoints] = useState<Record<string, string>>({});
  const saveRubric = useMutation({
    mutationFn: () => api.put('/api/admin/rubric', {
      income: (rubric.data?.income ?? [])
        .filter((o) => points[`i${o.id}`] !== undefined)
        .map((o) => ({ id: o.id, points: Number(points[`i${o.id}`]) })),
      siblings: (rubric.data?.siblings ?? [])
        .filter((o) => points[`s${o.id}`] !== undefined)
        .map((o) => ({ id: o.id, points: Number(points[`s${o.id}`]) })),
    }),
    onSuccess: () => { setPoints({}); refresh('rubric')(); },
  });

  if (sessionPending) return <main id="main"><Loading /></main>;
  if (!session?.admin) return <Navigate to="/admin/signin" replace />;
  if (!session.admin.canConfigure) {
    return (
      <>
        <AdminChrome admin={session.admin} />
        <main id="main">
          <Notice kind="info">
            Your account can read applications and download exports, but not change settings.
          </Notice>
          <p><Link to="/admin">← Back to applications</Link></p>
        </main>
      </>
    );
  }

  const currentCycle = cycles.data?.cycles.find((c) => c.isCurrent);

  return (
    <>
      <AdminChrome admin={session.admin} />
      <main id="main">
        <p><Link to="/admin">← Back to applications</Link></p>
        <h2>Settings</h2>

        <ErrorNotice
          error={setCycle.error ?? addEssay.error ?? retireEssay.error ?? saveRubric.error
                 ?? addAdmin.error ?? changeAdmin.error ?? checkMail.error ?? sendTest.error}
        />

        <h3>Scholarship years</h3>
        <p className="hint" style={{ marginTop: '-0.4rem' }}>
          The current year is the one applicants see. A year stays closed until you open it,
          even inside its dates — nothing starts accepting applications on its own. Opening a
          year takes effect immediately, whatever its opening date says.
          {' '}The award is what applicants are told the scholarship is worth; changing it here
          updates the landing page and the guidelines. Past years keep what they actually
          awarded.
        </p>
        <div className="panel scroll-x">
          <table>
            <thead>
              <tr>
                <th>Year</th><th>Opens</th><th>Closes</th>
                <th>Applications</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {(cycles.data?.cycles ?? []).slice(0, 6).map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.year}</strong>
                    {c.isCurrent && <> <span className="tag">current</span></>}
                  </td>
                  <td>{formatDate(c.opensAt)}</td>
                  <td>{formatDate(c.closesAt)}</td>
                  <td className="num">
                    {/* Editable in place: the award changes between cycles, and correcting a
                        figure quoted to applicants should not need a deploy. */}
                    <TextInput
                      type="number" min={0} step={500}
                      style={{ maxWidth: '7rem', textAlign: 'right' }}
                      defaultValue={c.awardAmount}
                      onBlur={(e) => {
                        const value = Number(e.target.value);
                        if (Number.isInteger(value) && value !== c.awardAmount) {
                          setCycle.mutate({ id: c.id, body: { awardAmount: value } });
                        }
                      }}
                    />
                  </td>
                  <td>{c.completed} of {c.total}</td>
                  <td>
                    <span className={`tag ${c.isOpen ? 'open' : 'shut'}`}>
                      {c.isOpen ? 'Accepting' : 'Closed'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="quiet"
                      disabled={setCycle.isPending}
                      onClick={() => setCycle.mutate({ id: c.id, body: { isOpen: !c.isOpen } })}
                    >
                      {c.isOpen ? 'Close' : 'Open'}
                    </button>
                    {!c.isCurrent && (
                      <button
                        className="quiet"
                        onClick={() => setCycle.mutate({ id: c.id, body: { isCurrent: true } })}
                      >
                        Make current
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3>Essay questions</h3>
        <p className="hint" style={{ marginTop: '-0.4rem' }}>
          Questions belong to a year. Retiring one hides it from new applicants and leaves
          existing answers intact.
        </p>
        {(essays.data?.essays ?? []).map((essay) => (
          <div className="entry" key={essay.id}>
            <header>
              <h4 style={{ fontWeight: 400 }}>{essay.question}</h4>
              <button
                className="quiet"
                disabled={!essay.active}
                onClick={() => {
                  if (window.confirm('Retire this question? Existing answers are kept.')) {
                    retireEssay.mutate(essay.id);
                  }
                }}
              >
                {essay.active ? 'Retire' : 'Retired'}
              </button>
            </header>
            <p className="hint" style={{ margin: 0 }}>
              {essay.max_length} character limit · {Number(essay.answer_count).toLocaleString()} answers already written
            </p>
          </div>
        ))}

        {currentCycle && (
          <form
            className="panel"
            onSubmit={(e) => { e.preventDefault(); addEssay.mutate(currentCycle.id); }}
          >
            <Field label={`New question for ${currentCycle.year}`}>
              <TextArea
                value={newQuestion}
                required
                style={{ minHeight: '5rem' }}
                onChange={(e) => setNewQuestion(e.target.value)}
              />
            </Field>
            <Field label="Character limit">
              <TextInput
                type="number"
                value={newLimit}
                min={100}
                onChange={(e) => setNewLimit(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            </Field>
            <div className="actions">
              <button type="submit" disabled={addEssay.isPending}>Add question</button>
            </div>
          </form>
        )}

        <h3>Scoring rubric</h3>
        <Notice kind="info">
          These points feed the objective total on the applications list. The values carried
          over from the old system had stopped matching the income options entirely, so every
          applicant was scoring zero for income — please review them before judging.
        </Notice>

        {rubric.isPending ? <Loading /> : (
          <div className="panel scroll-x">
            <table>
              <thead>
                <tr><th>Household income</th><th className="num">Points</th></tr>
              </thead>
              <tbody>
                {(rubric.data?.income ?? []).filter((o) => o.active).map((o) => (
                  <tr key={o.id}>
                    <td>{o.income}</td>
                    <td className="num">
                      <TextInput
                        type="number" min={0} step="0.5"
                        style={{ maxWidth: '6rem', textAlign: 'right' }}
                        value={points[`i${o.id}`] ?? String(o.points)}
                        onChange={(e) => setPoints({ ...points, [`i${o.id}`]: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
                <tr><th>Siblings in college</th><th className="num">Points</th></tr>
                {(rubric.data?.siblings ?? []).filter((o) => o.active).map((o) => (
                  <tr key={o.id}>
                    <td>{o.siblings}</td>
                    <td className="num">
                      <TextInput
                        type="number" min={0} step="0.5"
                        style={{ maxWidth: '6rem', textAlign: 'right' }}
                        value={points[`s${o.id}`] ?? String(o.points)}
                        onChange={(e) => setPoints({ ...points, [`s${o.id}`]: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="actions">
              <button
                disabled={Object.keys(points).length === 0 || saveRubric.isPending}
                onClick={() => saveRubric.mutate()}
              >
                {saveRubric.isPending ? 'Saving…' : 'Save rubric'}
              </button>
              {Object.keys(points).length > 0 && (
                <button className="secondary" onClick={() => setPoints({})}>Discard changes</button>
              )}
            </div>
          </div>
        )}

        <h3>Email</h3>
        <p className="hint" style={{ marginTop: '-0.4rem' }}>
          The application sends two messages: a temporary password when someone resets one, and
          a confirmation when an application is submitted. Check this after any change to the
          mail settings — an applicant discovering it on a deadline is the wrong way to find out.
        </p>

        {email.isPending ? <Loading /> : !email.data?.configured ? (
          <Notice kind="error">
            No mail server is configured, so password resets cannot be sent. Set the
            seed-smtp-host, seed-smtp-user and seed-smtp-password secrets.
          </Notice>
        ) : (
          <div className="panel">
            <dl className="dl" style={{ marginBottom: '1rem' }}>
              <dt>Server</dt><dd>{email.data.host}:{email.data.port}</dd>
              <dt>Signs in as</dt><dd>{email.data.user || '(no authentication)'}</dd>
              <dt>Sends from</dt><dd>{email.data.from}</dd>
              <dt>Password</dt><dd>{email.data.hasPassword ? 'set' : 'not set'}</dd>
            </dl>

            {mailResult && <Notice kind="ok">{mailResult}</Notice>}

            <div className="row">
              <Field label="Send a test message to" hint="Use an address you can check.">
                <TextInput
                  type="email" value={testTo} placeholder="you@example.org"
                  onChange={(e) => { setMailResult(null); setTestTo(e.target.value); }}
                />
              </Field>
              <Field label="Which message">
                <Select value={testKind} onChange={(e) => setTestKind(e.target.value)}>
                  <option value="plain">A short test message</option>
                  <option value="reset">The password reset email</option>
                  <option value="confirmation">The submission confirmation</option>
                </Select>
              </Field>
            </div>

            <div className="actions">
              <button
                disabled={!testTo || sendTest.isPending}
                onClick={() => { setMailResult(null); sendTest.mutate(); }}
              >
                {sendTest.isPending ? 'Sending…' : 'Send test message'}
              </button>
              <button
                className="secondary"
                disabled={checkMail.isPending}
                onClick={() => { setMailResult(null); checkMail.mutate(); }}
              >
                {checkMail.isPending ? 'Checking…' : 'Check connection only'}
              </button>
            </div>
            <p className="hint">
              Sending shows the real message an applicant would receive. Confirm it reaches the
              inbox rather than the spam folder — the domain has no DKIM or DMARC record yet,
              which makes spam classification more likely.
            </p>
          </div>
        )}

        <h3>Board and reviewer accounts</h3>
        <p className="hint" style={{ marginTop: '-0.4rem' }}>
          <strong>Reviewers</strong> read applications and download the PDFs and the scoring
          CSV. <strong>Administrators</strong> can also change everything on this page,
          including these accounts.
        </p>

        {admins.isPending ? <Loading /> : (
          <div className="panel scroll-x">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th><th>Last signed in</th><th />
                </tr>
              </thead>
              <tbody>
                {(admins.data?.admins ?? []).map((a) => (
                  <tr key={a.id} style={a.disabledAt ? { opacity: 0.55 } : undefined}>
                    <td>{a.name || '—'}</td>
                    <td>
                      {a.email}
                      {a.id === session.admin?.id && <> <span className="tag">you</span></>}
                    </td>
                    <td>
                      <span className="tag">{a.role}</span>
                      {a.disabledAt && <> <span className="tag shut">disabled</span></>}
                    </td>
                    <td>{a.lastLoginAt ? formatDate(a.lastLoginAt) : 'never'}</td>
                    <td>
                      {/* Disabling yourself, or the last administrator, is refused by the
                          server; the button is hidden here so it is not offered at all. */}
                      {a.id !== session.admin?.id && (
                        <button
                          className="quiet"
                          disabled={changeAdmin.isPending}
                          onClick={() => changeAdmin.mutate({
                            id: a.id, body: { disabled: !a.disabledAt },
                          })}
                        >
                          {a.disabledAt ? 'Re-enable' : 'Disable'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form
          className="panel"
          onSubmit={(e) => { e.preventDefault(); addAdmin.mutate(); }}
        >
          <h4 style={{ margin: '0 0 0.75rem' }}>Add an account</h4>
          <div className="row">
            <Field label="Email address">
              <TextInput
                type="email" required value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
              />
            </Field>
            <Field label="Name">
              <TextInput
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
              />
            </Field>
          </div>
          <div className="row">
            <Field label="Password" hint="At least 12 characters. Tell them out of band, and ask them to change it.">
              <TextInput
                type="password" required minLength={12} value={newAdmin.password}
                autoComplete="new-password"
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
              />
            </Field>
            <Field label="Role">
              <Select
                value={newAdmin.role}
                onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
              >
                <option value="reviewer">Reviewer — read and export</option>
                <option value="admin">Administrator — read, export, and configure</option>
              </Select>
            </Field>
          </div>
          <div className="actions">
            <button type="submit" disabled={addAdmin.isPending}>
              {addAdmin.isPending ? 'Creating…' : 'Create account'}
            </button>
            {addAdmin.isSuccess && <span className="hint">Account created.</span>}
          </div>
        </form>
      </main>
    </>
  );
}
